import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { updateMetaTags } from "../lib/seo";
import { Header } from "../components/Header";
import { Sidebar } from "../components/Sidebar";
import { SoundGenerator } from "../components/SoundGenerator";
import { SoundTable } from "../components/SoundGrid";
import { AudioPlayer } from "../components/AudioPlayer";
import { SettingsDialog } from "../components/SettingsDialog";
import { ExportDialog } from "../components/ExportDialog";
import {
  projectsApi,
  foldersApi,
  listProjects,
  listFolders,
  listSounds,
} from "../lib/api";
import type { Folder, SoundEffect, Project, Subscription } from "../types";
import { convertApiResponseToSoundEffect } from "../types";

function Generate() {
  const [user, setUser] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [projects, setProjects] = useState<Project[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [soundEffects, setSoundEffects] = useState<SoundEffect[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [currentSound, setCurrentSound] = useState<SoundEffect | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usageInfo, setUsageInfo] = useState<{
    remaining: number;
    isUnlimited: boolean;
    total: number;
    planType: string;
  } | null>(null);

  // Export State
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedSounds, setSelectedSounds] = useState<Set<string>>(new Set());
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Check auth state
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setUser(session?.user ?? null);
      },
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Load user data when authenticated
  useEffect(() => {
    if (user && selectedProject) {
      void loadFolders();
      void loadSoundEffects();
    }
  }, [selectedProject, currentFolder, user]);

  useEffect(() => {
    if (user) {
      void loadProjects();
      void loadSubscription();
      void loadUsageInfo();
    }
  }, [user]);

  // Update SEO based on current view
  useEffect(() => {
    if (settingsOpen) {
      updateMetaTags({
        title: "Settings | Bardus AI",
        description:
          "Configure your Bardus AI account settings and preferences.",
      });
    } else if (selectedProject) {
      const project = projects.find((p) => p.id === selectedProject);
      updateMetaTags({
        title: `${project?.name || "Project"} | Bardus AI`,
        description: `Managing sound effects for ${
          project?.name || "your project"
        } on Bardus AI.`,
      });
    } else {
      updateMetaTags({
        title: "Platform | Bardus AI",
        description:
          "Create and manage AI-generated sound effects for your game projects.",
      });
    }
  }, [settingsOpen, selectedProject, projects]);

  const loadProjects = async () => {
    try {
      const data = await listProjects();
      setProjects(data.projects || []);
      if (data.projects && data.projects.length > 0 && !selectedProject) {
        setSelectedProject(data.projects[0].id);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  const loadSubscription = async () => {
    try {
      // Mock subscription data - replace with actual API call
      setSubscription({
        id: "sub_1",
        status: "active",
        plan_type: "free",
        current_period_end: new Date().toISOString(),
        cancel_at_period_end: false,
      });
    } catch (error) {
      console.error("Error loading subscription:", error);
    }
  };

  const loadUsageInfo = async () => {
    try {
      // Mock usage data - replace with actual API call
      setUsageInfo({
        remaining: 8,
        total: 10,
        isUnlimited: false,
        planType: "Free",
      });
    } catch (error) {
      console.error("Error loading usage info:", error);
    }
  };

  const loadFolders = async () => {
    try {
      const data = await listFolders(selectedProject);
      setFolders(data.folders || []);
    } catch (error) {
      console.error("Error loading folders:", error);
    }
  };

  const loadSoundEffects = async () => {
    try {
      const data = await listSounds(
        selectedProject,
        currentFolder === null ? undefined : currentFolder,
      );
      const sounds = data.sounds?.map(convertApiResponseToSoundEffect) || [];
      setSoundEffects(sounds);
    } catch (error) {
      console.error("Error loading sounds:", error);
    }
  };

  // Export handlers
  const handleToggleSelection = (soundId: string) => {
    const newSelection = new Set(selectedSounds);
    if (newSelection.has(soundId)) {
      newSelection.delete(soundId);
    } else {
      newSelection.add(soundId);
    }
    setSelectedSounds(newSelection);
  };

  const handleBatchExport = () => {
    if (selectedSounds.size > 0) {
      setShowExportDialog(true);
    }
  };

  const handleExportClose = () => {
    setShowExportDialog(false);
    setSelectedSounds(new Set());
    setSelectionMode(false);
  };

  const handleProjectCreate = async (name: string) => {
    try {
      const response = await projectsApi.create({ name });
      const project = response.project;

      setProjects([...projects, project]);
      setSelectedProject(project.id);
    } catch (error: any) {
      console.error("Error creating project:", error);
      alert(`Failed to create project: ${error.message}`);
    }
  };

  const handleFolderCreate = async (name: string, icon: string) => {
    try {
      const folderData: any = {
        name,
        projectId: selectedProject,
        icon,
      };

      // Only add parentFolderId if currentFolder is not null
      if (currentFolder) {
        folderData.parentFolderId = currentFolder;
      }

      const response = await foldersApi.create(folderData);
      const folder = response.folder;

      setFolders([...folders, folder]);
    } catch (error: any) {
      console.error("Error creating folder:", error);
      alert(`Failed to create folder: ${error.message}`);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-foreground">
            Please sign in to continue
          </h1>
          <a
            href="/"
            className="text-primary hover:text-primary/80 transition-colors duration-200 underline underline-offset-4"
          >
            Return to homepage
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background text-foreground">
      <Sidebar
        selectedProjectId={selectedProject}
        projects={projects}
        folders={folders}
        selectedFolderId={currentFolder}
        onSelectProject={setSelectedProject}
        onCreateProject={handleProjectCreate}
        onSelectFolder={setCurrentFolder}
        onCreateFolder={handleFolderCreate}
        user={user}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          user={user}
          onSettingsOpen={() => setSettingsOpen(true)}
          subscription={subscription}
          usageInfo={usageInfo}
          selectionMode={selectionMode}
          onToggleSelectionMode={() => {
            setSelectionMode(!selectionMode);
            if (selectionMode) {
              setSelectedSounds(new Set());
            }
          }}
        />

        <main className="flex-1 overflow-auto p-6 pb-36">
          {selectedProject ? (
            <SoundTable
              sounds={soundEffects}
              loading={false}
              error={null}
              onPlaySound={(sound: SoundEffect) => {
                setCurrentSound(sound);
                setIsPlaying(true);
              }}
              onRefresh={loadSoundEffects}
              selectedFolderId={currentFolder}
              animationMode="none"
              newSoundId={undefined}
              selectionMode={selectionMode}
              selectedSounds={selectedSounds}
              onToggleSelection={handleToggleSelection}
              onBatchExport={handleBatchExport}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-foreground mb-2">
                  No project selected
                </h2>
                <p className="text-muted-foreground">
                  Create or select a project to get started
                </p>
              </div>
            </div>
          )}
        </main>

        {selectedProject && (
          <div className="fixed bottom-0 left-72 right-0 p-6">
            <SoundGenerator
              selectedProjectId={selectedProject}
              selectedFolderId={currentFolder}
              onRefreshSounds={loadSoundEffects}
            />
          </div>
        )}

        <AudioPlayer
          currentTrack={currentSound}
          isPlaying={isPlaying}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onStop={() => {
            setCurrentSound(null);
            setIsPlaying(false);
          }}
        />

        <SettingsDialog
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          user={user}
        />

        {/* Export Dialog */}
        {showExportDialog && (
          <ExportDialog
            selectedSounds={soundEffects.filter((s) =>
              selectedSounds.has(s.id),
            )}
            onClose={handleExportClose}
          />
        )}
      </div>
    </div>
  );
}

export default Generate;
